import { faCircleNotch, faFileZipper, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Button from '@/components/atoms/Button';

interface Props {
  /** Callback to trigger the download when complete */
  onDownload: () => void;
  /** Error message if creation failed */
  error?: string;
  /** Whether the export already exists and is ready */
  exportReady: boolean;
  /** Name of the item being exported (for display) */
  itemName: string;
  /** Optional: Additional description for the ready state */
  readyDescription?: string;
  /** Progress percentage (0-100) */
  progress?: number;
  /** Total number of documents being processed */
  totalDocuments?: number;
  /** Number of documents processed so far */
  processedDocuments?: number;
  /** Optional: Custom text for download button (defaults to "Download") */
  downloadButtonText?: string;
  /** Optional: Custom icon for download button */
  downloadButtonIcon?: React.ReactNode;
  /** Optional: Callback to retry on error (shows "Try Again" button) */
  onRetry?: () => void;
  /** Optional: Custom text for creating state */
  creatingText?: string;
}

/**
 * Generic export progress content component that returns JSX for any export operation.
 * Does NOT include Modal wrapper - parent should wrap in Modal if needed.
 * Fully controlled component - parent manages all state via props.
 *
 * @example
 * ```typescript
 * const [progress, setProgress] = useState(0);
 * const [exportReady, setExportReady] = useState(false);
 *
 * useExportEvents({
 *   progressEndpoint: '...',
 *   onEvent: (event) => {
 *     if (event.type.includes('progress')) {
 *       setProgress(event.progress);
 *     } else if (event.type.includes('complete')) {
 *       setExportReady(true);
 *     }
 *   }
 * });
 *
 * <Modal isOpen={isOpen} onClose={onClose}>
 *   <ExportProgressContent
 *     onDownload={handleDownload}
 *     exportReady={exportReady}
 *     progress={progress}
 *     totalDocuments={10}
 *     processedDocuments={5}
 *     itemName="Kim Answer"
 *   />
 * </Modal>
 * ```
 */
export function ExportProgressContent({
  onDownload,
  error,
  exportReady,
  itemName,
  readyDescription,
  progress = 0,
  totalDocuments = 0,
  processedDocuments = 0,
  downloadButtonText = 'Download',
  downloadButtonIcon,
  onRetry,
  creatingText = 'Creating export...',
}: Props) {
  // Error state
  if (error) {
    return (
      <div className="flex h-72 flex-col items-center justify-center gap-4 px-8 py-6">
        <div className="text-xl font-medium text-red-600">Error creating export</div>
        <div className="text-center text-gray-600">{error}</div>
        {onRetry && <Button onClick={onRetry} text="Try Again" type="secondary" className="mt-4" />}
      </div>
    );
  }

  // Ready state
  if (exportReady) {
    return (
      <div className="flex h-72 flex-col items-center justify-center gap-6 px-8 py-6">
        <FontAwesomeIcon icon={faCheckCircle} className="text-5xl text-green-500" />
        <div className="text-xl font-medium text-gray-700">Export ready for download</div>
        <div className="text-center text-gray-600">
          {readyDescription || `${itemName} with source documents has been packaged.`}
          {totalDocuments > 0 && (
            <div className="mt-1 text-sm text-gray-500">
              {totalDocuments} document{totalDocuments !== 1 ? 's' : ''}
            </div>
          )}
        </div>
        <Button
          onClick={onDownload}
          text={downloadButtonText}
          type="primary"
          rounded="md"
          className="w-full justify-center py-2.5"
          icon={downloadButtonIcon}
        />
      </div>
    );
  }

  // Creating state - always show progress bar (no separate "preparing" state)
  return (
    <div className="flex h-72 flex-col items-center justify-center gap-6 px-8 py-6">
      <FontAwesomeIcon icon={faFileZipper} className="text-brandSecondary text-5xl" />
      <div className="text-xl font-medium text-gray-700">{creatingText}</div>
      <div className="w-full max-w-md">
        <div className="mb-2 flex justify-between text-sm text-gray-600">
          <span>Progress</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="h-2.5 w-full rounded-full bg-gray-200">
          <div
            className="bg-brandSecondary h-2.5 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        {totalDocuments > 0 && (
          <div className="mt-2 text-center text-sm text-gray-600">
            Processing {processedDocuments} of {totalDocuments} document
            {totalDocuments !== 1 ? 's' : ''}
          </div>
        )}
      </div>
      <div className="text-center text-sm text-gray-600">
        This may take a few minutes depending on the number and size of documents.
      </div>
    </div>
  );
}
